/**
 * Positions Page
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPositions } from '../redux/slices/portfolioSlice';
import aliceBlueWebSocket from '../services/aliceBlueWebSocket';

const Positions = () => {
  const dispatch = useDispatch();
  const { positions, loading } = useSelector((state) => state.portfolio);
  const [livePrices, setLivePrices] = useState({});

  useEffect(() => {
    dispatch(fetchPositions());
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      dispatch(fetchPositions());
    }, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // WebSocket subscription for live prices
  useEffect(() => {
    if (!positions || positions.length === 0) return;

    // Extract unique symbols with exchange and token
    const symbols = positions
      .filter(pos => pos.Exchange && pos.Token)
      .map(pos => ({
        exchange: pos.Exchange,
        token: pos.Token
      }));

    if (symbols.length === 0) return;

    // Subscribe to WebSocket for live prices
    aliceBlueWebSocket.subscribe(symbols);

    // Setup price update callback
    const priceCallback = (data) => {
      const key = `${data.e}|${data.tk}`;
      setLivePrices(prev => ({
        ...prev,
        [key]: {
          ltp: data.lp,
          h: data.h,
          l: data.l,
          o: data.o,
          pc: data.pc,
          v: data.v
        }
      }));
    };

    // Add subscriber for each symbol
    symbols.forEach(symbol => {
      const key = `${symbol.exchange}|${symbol.token}`;
      aliceBlueWebSocket.addSubscriber(key, priceCallback);
    });

    // Cleanup on unmount
    return () => {
      aliceBlueWebSocket.unsubscribe(symbols);
      symbols.forEach(symbol => {
        const key = `${symbol.exchange}|${symbol.token}`;
        aliceBlueWebSocket.removeSubscriber(key, priceCallback);
      });
    };
  }, [positions]);

  // Calculate P&L with live prices
  const calculatePnL = (pos) => {
    const key = `${pos.Exchange}|${pos.Token}`;
    const livePrice = livePrices[key]?.ltp;
    const price = livePrice || pos.buyPrice || 0;
    const avgPrice = pos.netAveragePrice || 0;
    const qty = pos.netQuantity || 0;
    
    return (price - avgPrice) * qty;
  };

  const totalPnL = positions.reduce((sum, pos) => sum + calculatePnL(pos), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Positions</h1>
        <button onClick={() => dispatch(fetchPositions())} className="btn btn-primary">
          🔄 Refresh
        </button>
      </div>

      <div className="card">
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-600">Total P&L</h3>
          <p className={`text-3xl font-bold ${totalPnL >= 0 ? 'text-success' : 'text-danger'}`}>
            ₹{totalPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading positions...</p>
        ) : positions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-auto w-full">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Product</th>
                  <th>Net Qty</th>
                  <th>Avg Price</th>
                  <th>LTP</th>
                  <th>P&L</th>
                  <th>Day P&L</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos, idx) => {
                  const key = `${pos.Exchange}|${pos.Token}`;
                  const livePrice = livePrices[key]?.ltp;
                  const displayPrice = livePrice || pos.buyPrice || 0;
                  const pnl = calculatePnL(pos);
                  
                  return (
                    <tr key={idx}>
                      <td className="font-medium">{pos.tradingSymbol}</td>
                      <td>{pos.product}</td>
                      <td className={pos.netQuantity >= 0 ? 'text-success' : 'text-danger'}>
                        {pos.netQuantity}
                      </td>
                      <td>₹{pos.netAveragePrice?.toFixed(2)}</td>
                      <td className={livePrice ? 'text-blue-600 font-semibold' : ''}>
                        ₹{displayPrice?.toFixed(2)}
                        {livePrice && <span className="text-xs ml-1">●</span>}
                      </td>
                      <td className={pnl >= 0 ? 'text-success font-semibold' : 'text-danger font-semibold'}>
                        ₹{pnl?.toFixed(2)}
                      </td>
                      <td className={(pos.dayBuyValue - pos.daySellValue) >= 0 ? 'text-success' : 'text-danger'}>
                        ₹{(pos.dayBuyValue - pos.daySellValue)?.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-8 text-gray-500">No open positions</p>
        )}
      </div>
    </div>
  );
};

export default Positions;
